import { formatDistanceToNowStrict } from 'date-fns';
import { getLang } from './i18n';

/** Display formatting that respects the stable's time zone and the UI language. */
let tenantZone = 'Asia/Dubai';
let tenantCurrency = 'AED';

export function setTenantLocale(zone: string, currency: string): void {
  tenantZone = zone;
  tenantCurrency = currency;
}

export function zone(): string {
  return tenantZone;
}

const locale = () => (getLang() === 'ar' ? 'ar-AE' : 'en-AE');

export function money(amount: number | null | undefined, currency = tenantCurrency): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat(locale(), { style: 'currency', currency, maximumFractionDigits: amount % 100 === 0 ? 0 : 2 }).format(amount / 100);
}

/** Short form for chart axes: AED 12K. */
export function moneyCompact(amount: number | null | undefined, currency = tenantCurrency): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat(locale(), { style: 'currency', currency, notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(amount / 100);
}

export function number(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat(locale(), { maximumFractionDigits: digits }).format(n);
}

export function percent(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const v = n > 1 ? n / 100 : n;
  return new Intl.NumberFormat(locale(), { style: 'percent', maximumFractionDigits: digits }).format(v);
}

function asDate(v: string | number | Date | null | undefined): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function dateTime(v: string | Date | null | undefined, opts: Intl.DateTimeFormatOptions = {}): string {
  const d = asDate(v);
  if (!d) return '—';
  return new Intl.DateTimeFormat(locale(), { timeZone: tenantZone, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', ...opts }).format(d);
}

export function date(v: string | Date | null | undefined): string {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split('-').map(Number);
    return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
  }
  const d = asDate(v);
  return d ? new Intl.DateTimeFormat(locale(), { timeZone: tenantZone, day: 'numeric', month: 'short', year: 'numeric' }).format(d) : '—';
}

export function time(v: string | Date | null | undefined): string {
  const d = asDate(v);
  return d ? new Intl.DateTimeFormat(locale(), { timeZone: tenantZone, hour: '2-digit', minute: '2-digit' }).format(d) : '—';
}

export function weekday(v: string | Date | null | undefined, style: 'short' | 'long' = 'short'): string {
  const d = asDate(v);
  return d ? new Intl.DateTimeFormat(locale(), { timeZone: tenantZone, weekday: style }).format(d) : '—';
}

export function relative(v: string | Date | null | undefined): string {
  const d = asDate(v);
  if (!d) return '—';
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

export function duration(seconds: number | null | undefined): string {
  if (!seconds && seconds !== 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h ? `${h}h ${m.toString().padStart(2, '0')}m` : `${m} min`;
}

/** Local calendar date (YYYY-MM-DD) in the stable's zone. */
export function localIsoDate(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tenantZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Convert an <input type="datetime-local"> value (tenant wall time) to an ISO instant, and back. */
export function wallToIso(wall: string): string {
  if (!wall) return '';
  const [d, t] = wall.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = (t ?? '00:00').split(':').map(Number);
  const guess = Date.UTC(y, mo - 1, da, h, mi);
  const offset = zoneOffsetMinutes(new Date(guess));
  return new Date(guess - offset * 60_000).toISOString();
}

export function isoToWall(iso: string | null | undefined): string {
  const d = asDate(iso);
  if (!d) return '';
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tenantZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function zoneOffsetMinutes(at: Date): number {
  const wall = isoToWall(at.toISOString());
  const [d, t] = wall.split('T');
  const [y, mo, da] = d.split('-').map(Number);
  const [h, mi] = t.split(':').map(Number);
  return Math.round((Date.UTC(y, mo - 1, da, h, mi) - at.getTime()) / 60_000);
}
